import { parseZip } from 'shpjs';

/**
 * Error thrown when shapefile parsing fails.
 */
export class ShapefileParseError extends Error {
  constructor(
    message: string,
    public readonly cause?: unknown
  ) {
    super(message);
    this.name = 'ShapefileParseError';
  }
}

/**
 * Converts Shapefile data (ZIP archive or raw .shp buffer) to a GeoJSON FeatureCollection.
 *
 * Handles:
 * - Zipped shapefile archives (.zip containing .shp, .dbf, .prj, etc.)
 * - Individual .shp file buffers (geometry only, no attributes)
 *
 * Uses shpjs (shapefile-js) for parsing. Preserves feature geometries and attributes.
 *
 * @param data - ArrayBuffer containing the shapefile ZIP archive or raw .shp content
 * @returns Promise resolving to a GeoJSON FeatureCollection
 * @throws ShapefileParseError if the data cannot be parsed
 *
 * Validates: Requirements 9.3
 */
export async function toGeoJSON(data: ArrayBuffer): Promise<GeoJSON.FeatureCollection> {
  if (!data || data.byteLength === 0) {
    throw new ShapefileParseError('Input data is empty or undefined');
  }

  try {
    const result = await parseZip(data);
    return normalizeResult(result);
  } catch (error: unknown) {
    // If parseZip fails, provide a meaningful error message
    const message = error instanceof Error ? error.message : String(error);

    // Detect common failure modes
    if (message.includes('no layers founds') || message.includes('no layers found')) {
      throw new ShapefileParseError(
        'No shapefile layers found in the archive. Ensure the ZIP contains .shp files.',
        error
      );
    }

    if (message.includes('forgot to pass buffer')) {
      throw new ShapefileParseError('Invalid buffer data provided', error);
    }

    throw new ShapefileParseError(`Failed to parse shapefile: ${message}`, error);
  }
}

/**
 * Normalizes the shpjs result into a single GeoJSON FeatureCollection.
 *
 * shpjs can return:
 * - A single FeatureCollection (when ZIP contains one shapefile)
 * - An array of FeatureCollections (when ZIP contains multiple shapefiles)
 *
 * In the multi-layer case, all features are merged into a single FeatureCollection.
 */
function normalizeResult(result: unknown): GeoJSON.FeatureCollection {
  if (Array.isArray(result)) {
    // Multiple layers in the ZIP - merge all features into one collection
    const features: GeoJSON.Feature[] = [];
    for (const layer of result) {
      if (isFeatureCollection(layer)) {
        features.push(...layer.features);
      }
    }
    return {
      type: 'FeatureCollection',
      features,
    };
  }

  if (isFeatureCollection(result)) {
    return result;
  }

  throw new ShapefileParseError(
    'Unexpected result format from shapefile parser. Expected a FeatureCollection.'
  );
}

/**
 * Type guard to check if a value is a GeoJSON FeatureCollection.
 */
function isFeatureCollection(value: unknown): value is GeoJSON.FeatureCollection {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    (value as Record<string, unknown>).type === 'FeatureCollection' &&
    'features' in value &&
    Array.isArray((value as Record<string, unknown>).features)
  );
}
